// frontend/src/pages/admin/AdminSites.jsx
import { useAuth } from '../../utils/auth';
import { useState, useEffect, useCallback } from 'react';
import { sites as sitesApi, auth as authApi, rooms as roomsApi } from '../../utils/api';
import api from '../../utils/api';
import { Plus, Edit2, MapPin, Users, DoorOpen, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import ConfirmModal from '../../components/ConfirmModal';

const COLORS = ['#7c5cfc','#00d2b4','#f5a623','#ff4d6d','#22d3a0','#3b82f6','#a387fd','#f97316'];

function SiteModal({ site, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: site?.name||'', address: site?.address||'',
    color: site?.color||'#7c5cfc', displayOrder: site?.display_order||0,
    posEnabled: site?.pos_enabled !== false,
  });
  const [loading, setLoading] = useState(false);
  const f = k => ({value:form[k], onChange:e=>setForm(p=>({...p,[k]:e.target.value}))});

  const submit = async e => {
    e.preventDefault();
    setLoading(true);
    try {
      if (site) await sitesApi.update(site.id, form);
      else await sitesApi.create(form);
      toast.success(site?'Site mis à jour':'Site créé');
      onSaved(); onClose();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal">
        <h3 className="modal-title">{site?'Modifier le site':'Nouveau site'}</h3>
        <form onSubmit={submit}>
          <div className="form-group">
            <label className="form-label">Nom *</label>
            <input className="input" required {...f('name')} placeholder="Ex: Site Centre-ville"/>
          </div>
          <div className="form-group">
            <label className="form-label">Adresse</label>
            <textarea className="input" rows={2} {...f('address')} style={{resize:'vertical'}}
              placeholder="12 rue de la Paix, 75001 Paris"/>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Couleur</label>
              <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
                {COLORS.map(c=>(
                  <div key={c} onClick={()=>setForm(p=>({...p,color:c}))}
                    style={{width:28,height:28,borderRadius:'50%',background:c,cursor:'pointer',
                      border:form.color===c?'3px solid #fff':'3px solid transparent',
                      boxShadow:form.color===c?`0 0 0 2px ${c}`:'none'}}/>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Ordre d'affichage</label>
              <input type="number" className="input" min={0} {...f('displayOrder')}/>
            </div>
          </div>
          <div className="form-group">
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
              <input type="checkbox" checked={form.posEnabled}
                onChange={e=>setForm(p=>({...p,posEnabled:e.target.checked}))}/>
              Mode caisse activé sur ce site
            </label>
            <p style={{ fontSize:11, color:'var(--text-3)', marginTop:4 }}>
              Si désactivé, ce site n'apparaît plus dans le sélecteur du Mode Caisse.
            </p>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading?<span className="spinner" style={{width:16,height:16}}/>:(site?'Mettre à jour':'Créer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminSites() {
  const { can, canCreate } = useAuth();
  if (!can('admin.sites')) return <div className="page-body"><div className="empty-state card"><p>Accès non autorisé</p></div></div>;

  const [sites, setSites]   = useState([]);
  const [users, setUsers]   = useState([]);
  const [rooms, setRooms]   = useState([]);
  const [modal, setModal]   = useState(null);
  const [expanded, setExp]  = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [removing, setRemoving] = useState(false);

  const removeSite = async () => {
    setRemoving(true);
    try {
      await sitesApi.remove(confirmRemove.id);
      toast.success('Site supprimé');
      setConfirmRemove(null);
      load();
    } catch(e) { toast.error(e.response?.data?.error||'Erreur'); }
    finally { setRemoving(false); }
  };

  const load = useCallback(()=>{
    sitesApi.list().then(r=>setSites(r.data.sites));
    authApi.users().then(r=>setUsers(r.data.users));
    roomsApi.list().then(r=>setRooms(r.data.rooms));
  },[]);

  useEffect(()=>{load();},[load]);

  const assignUserSite = async (userId, siteId) => {
    try {
      await authApi.updateUser(userId, {siteId: siteId||null});
      toast.success('Site assigné');
      load();
    } catch { toast.error('Erreur'); }
  };

  const assignRoomSite = async (roomId, siteId) => {
    try {
      await roomsApi.update(roomId, {siteId: siteId||null});
      toast.success('Site assigné');
      load();
    } catch { toast.error('Erreur'); }
  };

  return (
    <>
      <div className="page-header">
        <div><h2>Gestion des sites</h2><p>Configuration des établissements et affectations</p></div>
        <button className="btn btn-primary"
          onClick={() => canCreate('multi_site', sites.length) ? setModal('new') : toast.error('Limite de sites atteinte pour votre plan')}
          disabled={!canCreate('multi_site', sites.length)}>
          <Plus size={15}/> Nouveau site
        </button>
      </div>

      <div className="page-body">
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))',gap:16,marginBottom:32}}>
          {sites.map(site=>(
            <div key={site.id} className="card" style={{borderLeft:`4px solid ${site.color}`}}>
              <div style={{display:'flex',alignItems:'flex-start',gap:12,marginBottom:12}}>
                <div style={{width:40,height:40,borderRadius:10,background:`${site.color}22`,
                  display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <MapPin size={18} color={site.color}/>
                </div>
                <div style={{flex:1}}>
                  <h3 style={{fontSize:16,fontWeight:700,marginBottom:2}}>{site.name}</h3>
                  {site.address && <p style={{fontSize:12,color:'var(--text-3)',lineHeight:1.4}}>{site.address}</p>}
                </div>
                <div style={{display:'flex',gap:4}}>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setModal(site)}>
                    <Edit2 size={14}/>
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={()=>setConfirmRemove(site)}>
                    <Trash2 size={14} color="var(--red)"/>
                  </button>
                </div>
              </div>
              <div style={{display:'flex',gap:16}}>
                <div style={{display:'flex',alignItems:'center',gap:5,fontSize:13}}>
                  <Users size={13} color="var(--text-3)"/>
                  <span>{site.user_count} collaborateur(s)</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:5,fontSize:13}}>
                  <DoorOpen size={13} color="var(--text-3)"/>
                  <span>{site.room_count} salle(s)</span>
                </div>
              </div>
              {site.pos_enabled === false && (
                <span style={{ display:'inline-block', marginTop:10, fontSize:10, fontWeight:700,
                  padding:'2px 8px', borderRadius:20, background:'rgba(255,77,109,0.12)', color:'var(--red)' }}>
                  Mode caisse désactivé
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Affectation des collaborateurs */}
        <div className="card" style={{marginBottom:20}}>
          <div className="card-title" style={{marginBottom:16}}>
            <Users size={15} style={{display:'inline',marginRight:6}}/>
            Affectation des collaborateurs
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Collaborateur</th><th>Rôle</th><th>Site par défaut</th></tr>
              </thead>
              <tbody>
                {users.filter(u=>u.role!=='freelance').map(u=>(
                  <tr key={u.id}>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div className="avatar" style={{width:28,height:28,fontSize:11}}>
                          {u.first_name?.[0]}{u.last_name?.[0]}
                        </div>
                        <span className="bold">{u.first_name} {u.last_name}</span>
                      </div>
                    </td>
                    <td><span className="badge badge-purple" style={{fontSize:11}}>{u.role}</span></td>
                    <td>
                      <select className="input" style={{width:'auto'}}
                        value={u.site_id||''} onChange={e=>assignUserSite(u.id,e.target.value)}>
                        <option value="">Aucun site</option>
                        {sites.map(s=>(
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Affectation des salles */}
        <div className="card">
          <div className="card-title" style={{marginBottom:16}}>
            <DoorOpen size={15} style={{display:'inline',marginRight:6}}/>
            Affectation des salles
          </div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Salle</th><th>Site</th></tr>
              </thead>
              <tbody>
                {rooms.map(room=>(
                  <tr key={room.id}>
                    <td className="bold">{room.name}</td>
                    <td>
                      <select className="input" style={{width:'auto'}}
                        value={room.site_id||''} onChange={e=>assignRoomSite(room.id,e.target.value)}>
                        <option value="">Aucun site</option>
                        {sites.map(s=>(
                          <option key={s.id} value={s.id}>
                            <span style={{color:s.color}}>■</span> {s.name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {modal && (
        <SiteModal
          site={modal==='new'?null:modal}
          onClose={()=>setModal(null)}
          onSaved={load}
        />
      )}
      {confirmRemove && (
        <ConfirmModal
          title="Supprimer le site"
          message={`Supprimer le site "${confirmRemove.name}" ?`}
          confirmLabel="Supprimer"
          danger
          loading={removing}
          onConfirm={removeSite}
          onCancel={() => setConfirmRemove(null)}
        />
      )}
    </>
  );
}
